package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.site.theme.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** One step of a coachmark tour: which element to spotlight + the bubble copy. */
data class CoachStep(val anchorId: String, val title: String, val description: String)

/** An active coachmark run. [onDone] fires when it's finished or dismissed (mark-seen). */
class CoachSequence(val steps: List<CoachStep>, val onDone: () -> Unit)

private fun dnum(v: dynamic): Double = v.unsafeCast<Double>()

/**
 * Native Compose-HTML coachmark — replaces Driver.js (which broke on the live
 * Compose page). Renders a full-screen dim with a rounded "spotlight" cutout over
 * the target element (pure CSS box-shadow), plus a branded tooltip with progress
 * and Back / Next / Got it / X. Buttons-only dismissal (background taps are
 * swallowed). Reads the `#cm-*` anchors' rects read-only; never mutates the page DOM.
 */
@Composable
fun CoachmarkHost(sequence: CoachSequence?) {
    if (sequence == null || sequence.steps.isEmpty()) return
    var index by remember(sequence) { mutableStateOf(0) }
    // Bumped on resize/scroll so the spotlight + tooltip re-measure.
    var tick by remember(sequence) { mutableStateOf(0) }

    DisposableEffect(sequence) {
        val cb: (org.w3c.dom.events.Event) -> Unit = { tick += 1 }
        window.addEventListener("resize", cb)
        window.addEventListener("scroll", cb, true)
        onDispose {
            window.removeEventListener("resize", cb)
            window.removeEventListener("scroll", cb, true)
        }
    }
    tick.hashCode() // read so recomposition re-measures

    // Hold the first paint until the page has truly settled. The search card mounts with a
    // `card-slide-in` entrance (translateY+fade, 0.3s) AFTER the heavy bundle hydrates, which
    // overlaps the coachmark's trigger — so measuring the anchor then catches it mid-slide and
    // flashes the coachmark in the wrong spot. Wait until (a) no `card-slide-in` is still running
    // AND (b) the anchor's rect has been unchanged for ~3 consecutive checks, then paint.
    fun entranceAnimating(): Boolean = try {
        val anims = document.asDynamic().getAnimations()
        val n = (anims.length as? Int) ?: 0
        var running = false
        var i = 0
        while (i < n) {
            val a = anims[i]
            if ((a.playState as? String) == "running" && (a.animationName as? String) == "card-slide-in") { running = true; break }
            i++
        }
        running
    } catch (_: Throwable) { false }

    var ready by remember(sequence) { mutableStateOf(false) }
    // Reveal gate (opacity) and movement gate (position transition). Both stay OFF on first paint:
    // the overlay mounts invisible and snaps to the anchor (no CSS transition) until its position is
    // confirmed stable, then fades in. This kills the "flash in the wrong spot, then slide to the
    // right spot" — that slide was the position transition animating the first re-measure. Deliberate
    // Back/Next navigation flips [animMoves] on just for that move, so steps still glide.
    var shown by remember(sequence) { mutableStateOf(false) }
    var animMoves by remember(sequence) { mutableStateOf(false) }
    LaunchedEffect(sequence) {
        val anchorId = sequence.steps.firstOrNull()?.anchorId
        var lastTop = Double.NaN; var lastLeft = Double.NaN
        var stable = 0; var elapsed = 0
        while (elapsed < 3000) {
            val el = anchorId?.let { document.getElementById(it) }
            val r = el?.let { it.asDynamic().getBoundingClientRect() }
            val top = if (r != null) dnum(r.top) else Double.NaN
            val left = if (r != null) dnum(r.left) else Double.NaN
            val rectStable = !top.isNaN() && top == lastTop && left == lastLeft
            if (rectStable && !entranceAnimating()) {
                stable += 1
                if (stable >= 3) break
            } else stable = 0
            lastTop = top; lastLeft = left
            kotlinx.coroutines.delay(60)
            elapsed += 60
        }
        ready = true
    }
    // After the overlay mounts (ready), confirm the anchor's rect is stable for a couple frames
    // WHILE STILL INVISIBLE — any late reflow right after mount is snapped to (transition is off),
    // not slid to — then fade the overlay in at the settled position. Afterwards keep re-measuring
    // so genuine later reflows (resize, scroll, async data, a re-played entrance) are tracked; those
    // also snap, since [animMoves] is only flipped on for deliberate Back/Next navigation.
    LaunchedEffect(ready) {
        if (!ready) return@LaunchedEffect
        val anchorId = sequence.steps.firstOrNull()?.anchorId
        var lastTop = Double.NaN; var lastLeft = Double.NaN; var stable = 0; var guard = 0
        while (stable < 2 && guard < 40) {
            tick += 1
            kotlinx.coroutines.delay(32)
            val el = anchorId?.let { document.getElementById(it) }
            val r = el?.let { it.asDynamic().getBoundingClientRect() }
            val top = if (r != null) dnum(r.top) else Double.NaN
            val left = if (r != null) dnum(r.left) else Double.NaN
            if (!top.isNaN() && top == lastTop && left == lastLeft) stable += 1 else stable = 0
            lastTop = top; lastLeft = left
            guard += 1
        }
        shown = true
        repeat(70) { kotlinx.coroutines.delay(50); tick += 1 }
    }
    // A deliberate step move (Back/Next) sets [animMoves]=true so that one move glides; reset it
    // shortly after so reflow re-measures go back to snapping (never sliding from a stale spot).
    LaunchedEffect(index) {
        if (animMoves) { kotlinx.coroutines.delay(340); animMoves = false }
    }

    val step = sequence.steps[index]
    val el = document.getElementById(step.anchorId)
    val vw = window.innerWidth.toDouble()
    val vh = window.innerHeight.toDouble()
    val rect = el?.let { it.asDynamic().getBoundingClientRect() }
    val hasRect = rect != null

    val pad = 6.0
    val rLeft = if (hasRect) dnum(rect.left) else 0.0
    val rTop = if (hasRect) dnum(rect.top) else 0.0
    val rW = if (hasRect) dnum(rect.width) else 0.0
    val rH = if (hasRect) dnum(rect.height) else 0.0
    val rBottom = rTop + rH
    val centerX = rLeft + rW / 2

    val tipW = minOf(328.0, vw - 24.0)
    // Prefer below the target; flip above when there isn't room.
    val placeBelow = !hasRect || (rBottom + 250 < vh)
    val tipLeft = minOf(maxOf(centerX - tipW / 2, 12.0), vw - tipW - 12.0)
    val arrowLeft = minOf(maxOf(centerX - tipLeft - 7.0, 18.0), tipW - 30.0)

    val isFirst = index == 0
    val isLast = index == sequence.steps.size - 1

    // Hold the first paint until the anchor's position has stabilized (see above).
    if (!ready) return

    // ---- Full-screen capture layer (blocks page interaction; buttons-only) ----
    Div(attrs = {
        style {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "5000")
            // transparent: the dim is painted by the spotlight box-shadow below
            property("background", "transparent")
            // Hold invisible until the spotlight's position has settled (see [shown]), then fade in
            // the whole overlay at once — never letting a mid-settle position be seen.
            property("opacity", if (shown) "1" else "0")
            property("transition", "opacity 0.18s ease")
        }
    }) {
        // ---- Spotlight: rounded transparent hole + dim everywhere else ----
        if (hasRect) {
            Div(attrs = {
                style {
                    property("position", "fixed")
                    property("left", "${rLeft - pad}px")
                    property("top", "${rTop - pad}px")
                    property("width", "${rW + pad * 2}px")
                    property("height", "${rH + pad * 2}px")
                    property("border-radius", "14px")
                    property("box-shadow", "0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 2px rgba(20,163,160,0.75)")
                    property("pointer-events", "none")
                    // Snap to position by default (reflow corrections must not slide); animate only
                    // during a deliberate Back/Next move.
                    property("transition", if (animMoves) "all 0.28s cubic-bezier(0.4,0,0.2,1)" else "none")
                }
            }) {}
        } else {
            // No target found — just dim the whole screen.
            Div(attrs = {
                style {
                    property("position", "fixed"); property("inset", "0")
                    property("background", "rgba(0,0,0,0.55)"); property("pointer-events", "none")
                }
            }) {}
        }

        // ---- Tooltip ----
        Div(attrs = {
            style {
                property("position", "fixed")
                property("left", "${tipLeft}px")
                if (placeBelow) {
                    property("top", "${rBottom + pad + 12}px")
                } else {
                    property("bottom", "${vh - (rTop - pad) + 12}px")
                }
                property("width", "${tipW}px")
                property("box-sizing", "border-box")
                property("background", "#ffffff")
                property("border-radius", "18px")
                property("box-shadow", "0 18px 48px rgba(14,124,123,0.22), 0 4px 14px rgba(0,0,0,0.10)")
                property("padding", "22px 22px 16px")
                property("z-index", "5001")
                property("font-family", TitleFontFamily)
                // Snap by default; glide only during a deliberate Back/Next move (see [animMoves]).
                property("transition", if (animMoves) "left 0.28s cubic-bezier(0.4,0,0.2,1), top 0.28s cubic-bezier(0.4,0,0.2,1)" else "none")
            }
        }) {
            // Arrow pointing at the target
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("left", "${arrowLeft}px")
                    property("width", "14px"); property("height", "14px")
                    property("background", "#ffffff")
                    property("transform", "rotate(45deg)")
                    if (placeBelow) property("top", "-7px") else property("bottom", "-7px")
                }
            }) {}

            // Close (X) — dismiss, mark seen
            Div(attrs = {
                onClick { sequence.onDone() }
                style {
                    property("position", "absolute"); property("top", "12px"); property("right", "14px")
                    property("color", "#b4bcc6"); property("font-size", "20px"); property("line-height", "1")
                    property("cursor", "pointer"); property("user-select", "none")
                }
            }) { Text("✕") }

            // Title
            H2(attrs = {
                style {
                    property("margin", "0 0 8px"); property("font-size", "18px"); property("font-weight", "800")
                    property("line-height", "1.25"); property("color", "#0E7C7B")
                    property("padding-right", "20px")
                }
            }) { Text(step.title) }

            // Description (supports <br> via raw text — Compose escapes, so keep plain)
            P(attrs = {
                style {
                    property("margin", "0"); property("font-size", "14px"); property("line-height", "1.55")
                    property("color", "#475569")
                }
            }) { Text(step.description) }

            // Footer: progress + Back / Next|Got it
            Div(attrs = {
                style {
                    property("display", "flex"); property("align-items", "center")
                    property("justify-content", "space-between"); property("margin-top", "18px"); property("gap", "10px")
                }
            }) {
                // progress (only for multi-step tours)
                if (sequence.steps.size > 1) {
                    Span(attrs = {
                        style {
                            property("font-size", "12px"); property("font-weight", "700")
                            property("color", "#94a3b8"); property("letter-spacing", "0.02em")
                        }
                    }) { Text("${index + 1} of ${sequence.steps.size}") }
                } else {
                    Span {}
                }

                Div(attrs = { style { property("display", "flex"); property("gap", "8px") } }) {
                    // Back (hidden on first step)
                    if (!isFirst) {
                        Button(attrs = {
                            onClick { if (index > 0) { animMoves = true; index -= 1 } }
                            style {
                                property("border", "none"); property("border-radius", "999px")
                                property("font-size", "13px"); property("font-weight", "700"); property("padding", "9px 18px")
                                property("cursor", "pointer"); property("color", "#0E7C7B"); property("background", "#E6F7F6")
                                property("font-family", TitleFontFamily)
                            }
                        }) { Text("Back") }
                    }
                    // Next / Got it
                    Button(attrs = {
                        onClick { if (isLast) sequence.onDone() else { animMoves = true; index += 1 } }
                        style {
                            property("border", "none"); property("border-radius", "999px")
                            property("font-size", "13px"); property("font-weight", "700"); property("padding", "9px 18px")
                            property("cursor", "pointer"); property("color", "#ffffff")
                            property("background", "linear-gradient(135deg,#0E7C7B 0%,#14A3A0 100%)")
                            property("box-shadow", "0 4px 12px rgba(14,124,123,0.30)")
                            property("font-family", TitleFontFamily)
                        }
                    }) { Text(if (isLast) "Got it" else "Next") }
                }
            }
        }
    }
}
